import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { Card, Form, HorizontalSortableList } from 'components';
import EmptyStateIcon from 'components/common/EmptyStateIcon';
import placeholderImage from 'assets/img/profile_placeholder.png';
import SelectRetailersModal from './SelectRetailersModal';
import * as Ui from './RetailersWidgetConfiguration.styles';

const RetailersWidgetFormFields = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });
  const { values, setFieldValue } = useFormikContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRetailers, setSelectedRetailers] = useState([]);

  // Sync selectedRetailers from formik values (for edit mode)
  useEffect(() => {
    if (values.retailers?.length > 0 && selectedRetailers.length === 0) {
      setSelectedRetailers(values.retailers);
    }
  }, [values.retailers, selectedRetailers.length]);

  const handleConfirmRetailers = (confirmedRetailers) => {
    setSelectedRetailers(confirmedRetailers);
    setFieldValue('retailers', confirmedRetailers);
  };

  const handleRemoveRetailer = (uid) => {
    setSelectedRetailers((prev) => prev.filter((r) => r.uid !== uid));
    setFieldValue(
      'retailers',
      (values.retailers || []).filter((r) => r.uid !== uid),
    );
  };

  const handleReorderRetailers = (reordered) => {
    setSelectedRetailers(reordered);
    setFieldValue('retailers', reordered);
  };

  return (
    <Col md={12}>
      <Form.GroupHorizontal label={t('selectRetailers')} required>
        {selectedRetailers.length > 0 ? (
          <HorizontalSortableList
            items={selectedRetailers}
            setItems={handleReorderRetailers}
            keyExtractor={(item) => item.uid}
            renderItem={(retailer) => (
              <Ui.RetailerCard>
                <Ui.RemoveButton
                  type="button"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleRemoveRetailer(retailer.uid);
                  }}
                >
                  &times;
                </Ui.RemoveButton>
                <Ui.RetailerImage
                  src={retailer.picture || placeholderImage}
                  alt={retailer.vanity_url}
                />
                <Ui.RetailerInfo>
                  {(retailer.name || retailer.last_name) && (
                    <Ui.RetailerName>
                      {[retailer.name, retailer.last_name].filter(Boolean).join(' ')}
                    </Ui.RetailerName>
                  )}
                </Ui.RetailerInfo>
              </Ui.RetailerCard>
            )}
            listFooter={<Ui.AddButton onClick={() => setIsModalOpen(true)}>+</Ui.AddButton>}
          />
        ) : (
          <Ui.EmptyState onClick={() => setIsModalOpen(true)}>
            <EmptyStateIcon />
            <Ui.EmptyMessage>{t('noRetailersSelected')}</Ui.EmptyMessage>
            <Ui.EmptySubMessage>{t('clickToAddRetailers')}</Ui.EmptySubMessage>
          </Ui.EmptyState>
        )}
      </Form.GroupHorizontal>

      <SelectRetailersModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmRetailers}
        initialSelected={selectedRetailers}
      />
    </Col>
  );
};

const RetailersWidgetConfiguration = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });

  return (
    <Card title={t('configuration')} subtitle={t('configurationSubtitle')} className="w-100">
      <Row>
        <RetailersWidgetFormFields />
      </Row>
    </Card>
  );
};

export default {
  titleKey: 'types.retailers',
  descriptionKey: 'types.retailersDescription',
  defaultValues: { retailers: [] },
  buildPayload: (values) => ({
    retailer_uids: (values.retailers || []).map((r) => r.uid),
  }),
  isValid: (values) => values.retailers?.length > 0,
  parseApiData: (widget) => ({
    retailers: widget.retailers || [],
  }),
  ConfigComponent: RetailersWidgetConfiguration,
};
